import React, { Suspense } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ClientProvider } from "@/contexts/ClientContext";
import { AuditorProvider } from "@/contexts/AuditorContext";
import { ClientPortalProvider } from "@/contexts/ClientPortalContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LoadingState } from "@/components/ui/LoadingState";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { StaffRoute } from "@/components/auth/StaffRoute";

// --- Lazy-loaded page components ---

// Client pages
const Clients = React.lazy(() => import("./pages/Clients"));
const ClientNew = React.lazy(() => import("./pages/ClientNew"));
const ClientEdit = React.lazy(() => import("./pages/ClientEdit"));
const ClientUsers = React.lazy(() => import("./pages/ClientUsers"));
const ClientUserNew = React.lazy(() => import("./pages/ClientUserNew"));
const ClientUserEdit = React.lazy(() => import("./pages/ClientUserEdit"));

// Per-client hub (CIR-2026-354): parent layout + section routes. The URL :id
// is the single source of truth for the current client.
const ClientHubLayout = React.lazy(() => import("./pages/clients/ClientHubLayout"));
const ClientHubIndex = React.lazy(() =>
  import("./pages/clients/ClientHubLayout").then((m) => ({ default: m.ClientHubIndex })),
);
const ClientOverview = React.lazy(() => import("./pages/clients/ClientOverview"));
const ClientGovernance = React.lazy(() => import("./pages/clients/ClientGovernance"));
const ClientCoverage = React.lazy(() => import("./pages/clients/ClientCoverage"));
const ClientDocumentsRedirect = React.lazy(() => import("./pages/redirects/ClientDocumentsRedirect"));

// Asset pages
const AssetOverview = React.lazy(() => import("./pages/AssetOverview"));
const PeopleAssets = React.lazy(() => import("./pages/assets/PeopleAssets"));
const PeopleAssetForm = React.lazy(() => import("./pages/assets/PeopleAssetForm"));
const PeopleAssetView = React.lazy(() => import("./pages/assets/PeopleAssetView"));
const PhysicalAssets = React.lazy(() => import("./pages/assets/PhysicalAssets"));
const PhysicalAssetForm = React.lazy(() => import("./pages/assets/PhysicalAssetForm"));
const PhysicalAssetView = React.lazy(() => import("./pages/assets/PhysicalAssetView"));
const SupplierAssets = React.lazy(() => import("./pages/assets/SupplierAssets"));
const SupplierAssetForm = React.lazy(() => import("./pages/assets/SupplierAssetForm"));
const SupplierAssetView = React.lazy(() => import("./pages/assets/SupplierAssetView"));
const DataAssets = React.lazy(() => import("./pages/assets/DataAssets"));
const DataAssetForm = React.lazy(() => import("./pages/assets/DataAssetForm"));
const DataAssetView = React.lazy(() => import("./pages/assets/DataAssetView"));
const HardwareAssets = React.lazy(() => import("./pages/assets/HardwareAssets"));
const HardwareAssetForm = React.lazy(() => import("./pages/assets/HardwareAssetForm"));
const HardwareAssetView = React.lazy(() => import("./pages/assets/HardwareAssetView"));
const SoftwareAssets = React.lazy(() => import("./pages/assets/SoftwareAssets"));
const SoftwareAssetForm = React.lazy(() => import("./pages/assets/SoftwareAssetForm"));
const SoftwareAssetView = React.lazy(() => import("./pages/assets/SoftwareAssetView"));
const ServiceAssets = React.lazy(() => import("./pages/assets/ServiceAssets"));
const ServiceAssetForm = React.lazy(() => import("./pages/assets/ServiceAssetForm"));
const ServiceAssetView = React.lazy(() => import("./pages/assets/ServiceAssetView"));
const CloudAssets = React.lazy(() => import("./pages/assets/CloudAssets"));

// DDQ pages
const DDQQuestions = React.lazy(() => import("./pages/DDQQuestions"));
const DDQResponses = React.lazy(() => import("./pages/DDQResponses"));
const DDQReview = React.lazy(() => import("./pages/DDQReview"));

// Risk pages
const RiskRegister = React.lazy(() => import("./pages/RiskRegister"));
const RiskMatrix = React.lazy(() => import("./pages/RiskMatrix"));
const RiskForm = React.lazy(() => import("./pages/RiskForm"));
const RiskDetail = React.lazy(() => import("./pages/RiskDetail"));
const RiskTreatments = React.lazy(() => import("./pages/RiskTreatments"));
const RiskAppetite = React.lazy(() => import("./pages/settings/RiskAppetite"));

// ISO / SoA pages
const IsoControls = React.lazy(() => import("./pages/IsoControls"));
const StatementOfApplicability = React.lazy(() => import("./pages/StatementOfApplicability"));
const ControlDetail = React.lazy(() => import("./pages/ControlDetail"));
const ControlRiskMapping = React.lazy(() => import("./pages/ControlRiskMapping"));

// Compliance pages
const ControllerActionItems = React.lazy(() => import("./pages/compliance/ControllerActionItems"));

// OSINT pages
const OSINTFindings = React.lazy(() => import("./pages/OSINTFindings"));
const OSINTFindingDetail = React.lazy(() => import("./pages/OSINTFindingDetail"));

// Legal pages
const PrivacyNotice = React.lazy(() => import("./pages/legal/PrivacyNotice"));
const LegalProfileSetup = React.lazy(() => import("./pages/legal/LegalProfileSetup"));
const LegalComplianceDashboard = React.lazy(() => import("./pages/legal/LegalComplianceDashboard"));
const LegalComplianceAudit = React.lazy(() => import("./pages/legal/LegalComplianceAudit"));

// ISMS pages
const IsmsDocumentLibrary = React.lazy(() => import("./pages/isms/IsmsDocumentLibrary"));
const DocumentProfileWizard = React.lazy(() => import("./pages/isms/DocumentProfileWizard"));
const ControlDocumentMap = React.lazy(() => import("./pages/isms/ControlDocumentMap"));

// Admin pages
const ReferenceDocuments = React.lazy(() => import("./pages/admin/ReferenceDocuments"));
const OsintLibrary = React.lazy(() => import("./pages/admin/OsintLibrary"));
const TemplateLibrary = React.lazy(() => import("./pages/admin/TemplateLibrary"));
const FrameworkTemplateLibrary = React.lazy(() => import("./pages/admin/FrameworkTemplateLibrary"));
const LegalRequirementsLibrary = React.lazy(() => import("./pages/admin/LegalRequirementsLibrary"));
const FrameworkEvidenceImport = React.lazy(() => import("./pages/admin/FrameworkEvidenceImport"));

// CIR-2026-523: Staff billing override (billing_admin / admin only, server-enforced)
const StaffBillingOverride = React.lazy(() => import("./pages/billing/StaffBillingOverride"));

// CIR-2026-543: Service Tier plan catalogue + assignment (Staff-only)
const ServicePlans = React.lazy(() => import("./pages/admin/ServicePlans"));

// CIR-2026-551: Guided Interface content management (Staff-only)
const GuidedContentManagement = React.lazy(() => import("./pages/staff/GuidedContentManagement"));

// CIR-2026-550: Staff-only aggregate de-identified guided-journey drop-off report (Epic 14 FR-42)
const GuidedDropoffReport = React.lazy(() => import("./pages/staff/GuidedDropoffReport"));

// CIR-2026-570: Staff-only schema map (live table relationships + derived-data pipelines)
const SchemaMap = React.lazy(() => import("./pages/staff/SchemaMap"));

// CIR-2026-520: Billing frontend
const PricingPage = React.lazy(() => import("./pages/billing/PricingPage"));
const Signup = React.lazy(() => import("./pages/billing/Signup"));
const BillingPage = React.lazy(() => import("./pages/billing/BillingPage"));

// CIR pages (individual files — barrel re-export not compatible with React.lazy)
const CIRDashboard = React.lazy(() => import("./pages/cir/CIRDashboard"));
const CIRSubmit = React.lazy(() => import("./pages/cir/CIRSubmit"));
const CIRDetail = React.lazy(() => import("./pages/cir/CIRDetail"));

// Audit / NCR pages (M5 Internal Audit Management)
const AuditProgramme = React.lazy(() => import("./pages/audit/AuditProgramme"));
const AuditDetail = React.lazy(() => import("./pages/audit/AuditDetail"));
const AuditForm = React.lazy(() => import("./pages/audit/AuditForm"));
const NCRList = React.lazy(() => import("./pages/audit/NCRList"));
const NCRDetail = React.lazy(() => import("./pages/audit/NCRDetail"));

// Incident Management pages (M4)
const IncidentDashboard = React.lazy(() => import("./pages/incidents/IncidentDashboard"));
const IncidentDetail = React.lazy(() => import("./pages/incidents/IncidentDetail"));
const IncidentForm = React.lazy(() => import("./pages/incidents/IncidentForm"));

// Training & Awareness pages (M6)
const TrainingDashboard = React.lazy(() => import("./pages/training/TrainingDashboard"));
const TrainingDetail = React.lazy(() => import("./pages/training/TrainingDetail"));
const TrainingForm = React.lazy(() => import("./pages/training/TrainingForm"));
const TrainingTypes = React.lazy(() => import("./pages/training/TrainingTypes"));
const CampaignList = React.lazy(() => import("./pages/training/CampaignList"));
const CampaignDetail = React.lazy(() => import("./pages/training/CampaignDetail"));
const CompetencyMatrix = React.lazy(() => import("./pages/training/CompetencyMatrix"));

// Business Continuity pages (M7)
const BCPDashboard = React.lazy(() => import("./pages/bcp/BCPDashboard"));
const ProcessForm = React.lazy(() => import("./pages/bcp/ProcessForm"));
const ProcessDetail = React.lazy(() => import("./pages/bcp/ProcessDetail"));
const BCPPlanList = React.lazy(() => import("./pages/bcp/BCPPlanList"));
const PlanForm = React.lazy(() => import("./pages/bcp/PlanForm"));
const PlanDetail = React.lazy(() => import("./pages/bcp/PlanDetail"));
const TestList = React.lazy(() => import("./pages/bcp/TestList"));
const TestForm = React.lazy(() => import("./pages/bcp/TestForm"));
const TestDetail = React.lazy(() => import("./pages/bcp/TestDetail"));
const BackupStatus = React.lazy(() => import("./pages/bcp/BackupStatus"));

// New feature pages (CIR-075, CIR-154, CIR-162, CIR-163, CIR-164)
const KEVCatalog = React.lazy(() => import("./pages/KEVCatalog"));
const ISMSDocTracker = React.lazy(() => import("./pages/ISMSDocTracker"));
const SupplierRiskAssessment = React.lazy(() => import("./pages/SupplierRiskAssessment"));
const ISMSDocumentLibraryBrowse = React.lazy(() => import("./pages/ISMSDocumentLibraryBrowse"));

// M10: Consolidated Audit Pack
const AuditPackGenerator = React.lazy(() => import("./pages/AuditPackGenerator"));

// M9: Auditor Portal pages
const AuditorLogin = React.lazy(() => import("./pages/auditor/AuditorLogin"));
const AuditorDashboard = React.lazy(() => import("./pages/auditor/AuditorDashboard"));
const AuditorEvidence = React.lazy(() => import("./pages/auditor/AuditorEvidence"));

// L4: Client Portal pages
const ClientLogin = React.lazy(() => import("./pages/client-portal/ClientLogin"));
const ClientSetPassword = React.lazy(() => import("./pages/client-portal/ClientSetPassword"));
const ClientResetPassword = React.lazy(() => import("./pages/client-portal/ClientResetPassword"));
const ClientDashboard = React.lazy(() => import("./pages/client-portal/ClientDashboard"));
// CIR-2026-546: Guided Checklist Engine shell — every client user lands here
// at login regardless of service tier (FR-13-FR-15a, UC-04).
const GuidedEngine = React.lazy(() => import("./pages/client-portal/GuidedEngine"));
// CIR-2026-556: certification-record capture/edit/status surface (prepare+
// track, declare-existing) — the full journey-tracker homepage is separate
// (CIR-558/559).
const GuidedCertifications = React.lazy(() => import("./pages/client-portal/GuidedCertifications"));
const ClientAccount = React.lazy(() => import("./pages/client-portal/ClientAccount"));
const ClientNotifications = React.lazy(() => import("./pages/client-portal/ClientNotifications"));
// CIR-2026-528: Self-serve billing management for client portal admins
const ClientBilling = React.lazy(() => import("./pages/client-portal/ClientBilling"));

// Management Review pages (M8)
const ManagementReviewDashboard = React.lazy(() => import("./pages/management-review/ManagementReviewDashboard"));
const ReviewList = React.lazy(() => import("./pages/management-review/ReviewList"));
const ReviewForm = React.lazy(() => import("./pages/management-review/ReviewForm"));
const ReviewDetail = React.lazy(() => import("./pages/management-review/ReviewDetail"));
const KPIConfiguration = React.lazy(() => import("./pages/management-review/KPIConfiguration"));
const KPITrends = React.lazy(() => import("./pages/management-review/KPITrends"));

// CIR-2026-405: one QueryClient per authentication context / portal.
//
// The app hosts three separate auth domains that must NEVER share a React
// Query cache within a single browser session:
//   - staff          Supabase staff session (the main console, top-level tree)
//   - auditor        token-based AuditorProvider (/auditor/*)
//   - client-portal  Supabase client_users session (/client-portal/*)
//
// Query keys across these trees reuse generic prefixes (e.g. ["soa", ...],
// ["notifications", ...]) and the global staleTime is 30s, so a single shared
// client would let one portal's cached rows surface in another after an in-tab
// portal transition. Giving each portal route tree its own QueryClientProvider
// isolates the caches at the infrastructure level, so no per-key portal
// namespacing discipline is required. This is the project's cross-portal
// cache-isolation convention — see CLAUDE.md "Frontend Patterns".
const createAppQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 30_000,
      },
    },
  });

const staffQueryClient = createAppQueryClient();
const auditorQueryClient = createAppQueryClient();
const clientPortalQueryClient = createAppQueryClient();

const App = () => (
  <QueryClientProvider client={staffQueryClient}>
    <TooltipProvider>
      <ClientProvider>
        <BrowserRouter>
          <Toaster />
          <ScrollToTop />
          <ErrorBoundary>
          <Suspense fallback={<LoadingState label="Loading page..." rows={3} />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />

            {/* Public privacy notice — must be reachable without authentication
                (ICO right-to-be-informed; referenced by the DPA and
                sub-processor list for external readers). Authenticated users
                reach it too (e.g. the ClientAccount page links here). */}
            <Route path="/privacy" element={<PrivacyNotice />} />

            {/* CIR-2026-520: Public billing routes — no auth required */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/signup" element={<Signup />} />

            {/* All routes below require authentication */}
            <Route element={<ProtectedRoute />}>
              {/* CIR-2026-357: the staff client console. /clients and ALL
                  /clients/* routes are gated behind StaffRoute (staff role),
                  not just ProtectedRoute (session) — defence-in-depth on top of
                  the RLS that already bars client writes. Client-facing users
                  use the separate Client Portal (/client-portal/*); non-staff
                  sessions reaching here are redirected to the portal login. */}
              <Route element={<StaffRoute />}>
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/new" element={<ClientNew />} />

                {/* CIR-2026-354: per-client hub. The :id parent layout owns the
                    chrome (AppLayout, client header, breadcrumb, sub-nav) and
                    feeds the URL-derived client to its section children via the
                    Outlet context — selectedClient is never read for nav. */}
                <Route path="/clients/:id" element={<ClientHubLayout />}>
                  <Route index element={<ClientHubIndex />} />
                  <Route path="overview" element={<ClientOverview />} />
                  <Route path="governance" element={<ClientGovernance />} />
                  <Route path="coverage" element={<ClientCoverage />} />
                  <Route path="users" element={<ClientUsers />} />
                  <Route path="isms-library" element={<IsmsDocumentLibrary />} />
                  <Route path="legal-compliance" element={<LegalComplianceDashboard />} />
                  <Route path="audit-pack" element={<AuditPackGenerator />} />
                </Route>

                {/* CIR-2026-351: bookmarked/typed /documents URLs resolve to the
                    ISMS Library instead of the 404 catch-all. */}
                <Route path="/clients/:id/documents" element={<ClientDocumentsRedirect />} />

                {/* Standalone client routes (own their own AppLayout — not hub sections) */}
                <Route path="/clients/:id/edit" element={<ClientEdit />} />
                <Route path="/clients/:id/users/new" element={<ClientUserNew />} />
                <Route path="/clients/:id/users/:user_id/edit" element={<ClientUserEdit />} />
                <Route path="/clients/:id/legal-profile" element={<LegalProfileSetup />} />
                <Route path="/clients/:id/legal-compliance/audit" element={<LegalComplianceAudit />} />
                <Route path="/clients/:id/document-profile" element={<DocumentProfileWizard />} />
                {/* CIR-2026-535: per-client SoA control detail is a staff-console page —
                    gate it behind StaffRoute for parity with all other /clients/* routes
                    (a client-portal user is redirected to /client-login). */}
                <Route path="/clients/:clientId/soa/:controlRef" element={<ControlDetail />} />
              </Route>

              <Route path="/assets" element={<AssetOverview />} />
              <Route path="/assets/people" element={<PeopleAssets />} />
              <Route path="/assets/people/new" element={<PeopleAssetForm />} />
              <Route path="/assets/people/:id" element={<PeopleAssetView />} />
              <Route path="/assets/people/:id/edit" element={<PeopleAssetForm />} />
              <Route path="/assets/physical" element={<PhysicalAssets />} />
              <Route path="/assets/physical/new" element={<PhysicalAssetForm />} />
              <Route path="/assets/physical/:id" element={<PhysicalAssetView />} />
              <Route path="/assets/physical/:id/edit" element={<PhysicalAssetForm />} />
              <Route path="/assets/suppliers" element={<SupplierAssets />} />
              <Route path="/assets/suppliers/new" element={<SupplierAssetForm />} />
              <Route path="/assets/suppliers/:id" element={<SupplierAssetView />} />
              <Route path="/assets/suppliers/:id/edit" element={<SupplierAssetForm />} />
              <Route path="/assets/data" element={<DataAssets />} />
              <Route path="/assets/data/new" element={<DataAssetForm />} />
              <Route path="/assets/data/:id" element={<DataAssetView />} />
              <Route path="/assets/data/:id/edit" element={<DataAssetForm />} />
              <Route path="/assets/hardware" element={<HardwareAssets />} />
              <Route path="/assets/hardware/new" element={<HardwareAssetForm />} />
              <Route path="/assets/hardware/:id" element={<HardwareAssetView />} />
              <Route path="/assets/hardware/:id/edit" element={<HardwareAssetForm />} />
              <Route path="/assets/software" element={<SoftwareAssets />} />
              <Route path="/assets/software/new" element={<SoftwareAssetForm />} />
              <Route path="/assets/software/:id" element={<SoftwareAssetView />} />
              <Route path="/assets/software/:id/edit" element={<SoftwareAssetForm />} />
              <Route path="/assets/services" element={<ServiceAssets />} />
              <Route path="/assets/services/new" element={<ServiceAssetForm />} />
              <Route path="/assets/services/:id" element={<ServiceAssetView />} />
              <Route path="/assets/services/:id/edit" element={<ServiceAssetForm />} />
              <Route path="/assets/cloud" element={<CloudAssets />} />
              <Route path="/ddq-responses" element={<DDQResponses />} />
              <Route path="/ddq-review" element={<DDQReview />} />
              <Route path="/risk-register" element={<RiskRegister />} />
              <Route path="/risk-register/new" element={<RiskForm />} />
              <Route path="/risk-register/:id" element={<RiskDetail />} />
              <Route path="/risk-register/:id/edit" element={<RiskForm />} />
              <Route path="/risk-matrix" element={<RiskMatrix />} />
              <Route path="/risk-treatments" element={<RiskTreatments />} />
              <Route path="/settings/risk-appetite" element={<RiskAppetite />} />
              <Route path="/soa" element={<StatementOfApplicability />} />
              <Route path="/osint-findings" element={<OSINTFindings />} />
              <Route path="/osint-findings/:id" element={<OSINTFindingDetail />} />
              <Route path="/cir" element={<CIRDashboard />} />
              <Route path="/cir/submit" element={<CIRSubmit />} />
              <Route path="/cir/:id" element={<CIRDetail />} />
              {/* CIR-2026-520: Billing dashboard (authenticated) */}
              <Route path="/billing" element={<BillingPage />} />

              <Route path="/kev-catalog" element={<KEVCatalog />} />
              <Route path="/isms-doc-tracker" element={<ISMSDocTracker />} />
              <Route path="/supplier-risk-assessment" element={<SupplierRiskAssessment />} />
              <Route path="/isms-document-library" element={<ISMSDocumentLibraryBrowse />} />

              {/* M5: Internal Audit Management */}
              <Route path="/audits" element={<AuditProgramme />} />
              <Route path="/audits/new" element={<AuditForm />} />
              <Route path="/audits/:auditId" element={<AuditDetail />} />
              <Route path="/audits/:auditId/edit" element={<AuditForm />} />
              <Route path="/ncrs" element={<NCRList />} />
              <Route path="/ncrs/:ncrId" element={<NCRDetail />} />

              {/* M4: Incident Management */}
              <Route path="/incidents" element={<IncidentDashboard />} />
              <Route path="/incidents/new" element={<IncidentForm />} />
              <Route path="/incidents/:incidentId" element={<IncidentDetail />} />
              <Route path="/incidents/:incidentId/edit" element={<IncidentForm />} />

              {/* M6: Training & Awareness */}
              <Route path="/training" element={<TrainingDashboard />} />
              <Route path="/training/new" element={<TrainingForm />} />
              <Route path="/training/types" element={<TrainingTypes />} />
              <Route path="/training/campaigns" element={<CampaignList />} />
              <Route path="/training/campaigns/:campaignId" element={<CampaignDetail />} />
              <Route path="/training/competency" element={<CompetencyMatrix />} />
              <Route path="/training/:recordId" element={<TrainingDetail />} />
              <Route path="/training/:recordId/edit" element={<TrainingForm />} />

              {/* M7: Business Continuity */}
              <Route path="/business-continuity" element={<BCPDashboard />} />
              <Route path="/business-continuity/processes/new" element={<ProcessForm />} />
              <Route path="/business-continuity/processes/:processId" element={<ProcessDetail />} />
              <Route path="/business-continuity/processes/:processId/edit" element={<ProcessForm />} />
              <Route path="/business-continuity/plans" element={<BCPPlanList />} />
              <Route path="/business-continuity/plans/new" element={<PlanForm />} />
              <Route path="/business-continuity/plans/:planId" element={<PlanDetail />} />
              <Route path="/business-continuity/plans/:planId/edit" element={<PlanForm />} />
              <Route path="/business-continuity/tests" element={<TestList />} />
              <Route path="/business-continuity/tests/new" element={<TestForm />} />
              <Route path="/business-continuity/tests/:testId" element={<TestDetail />} />
              <Route path="/business-continuity/tests/:testId/edit" element={<TestForm />} />
              <Route path="/business-continuity/backups" element={<BackupStatus />} />

              {/* M8: Management Review */}
              <Route path="/management-review" element={<ManagementReviewDashboard />} />
              <Route path="/management-review/reviews" element={<ReviewList />} />
              <Route path="/management-review/reviews/new" element={<ReviewForm />} />
              <Route path="/management-review/reviews/:reviewId" element={<ReviewDetail />} />
              <Route path="/management-review/kpis" element={<KPIConfiguration />} />
              <Route path="/management-review/kpi-trends" element={<KPITrends />} />

              {/* CIR-2026-147: Staff-only routes (SecuraPath users only) */}
              <Route element={<StaffRoute />}>
                <Route path="/admin/osint-library" element={<OsintLibrary />} />
                <Route path="/admin/reference-documents" element={<ReferenceDocuments />} />
                <Route path="/admin/template-library" element={<TemplateLibrary />} />
                <Route path="/admin/framework-template-library" element={<FrameworkTemplateLibrary />} />
                <Route path="/admin/legal-requirements-library" element={<LegalRequirementsLibrary />} />
                <Route path="/admin/framework-evidence-import" element={<FrameworkEvidenceImport />} />
                <Route path="/iso-controls" element={<IsoControls />} />
                <Route path="/ddq-questions" element={<DDQQuestions />} />
                <Route path="/control-document-map" element={<ControlDocumentMap />} />
                <Route path="/control-risk-mapping" element={<ControlRiskMapping />} />
                <Route path="/compliance/controller-actions" element={<ControllerActionItems />} />
                {/* CIR-2026-523: Staff billing override — gated to billing_admin/admin by the Edge Function */}
                <Route path="/staff/billing" element={<StaffBillingOverride />} />
                {/* CIR-2026-543: plan catalogue + assign-plan (Epic 15 FR-01/FR-02) */}
                <Route path="/staff/service-plans" element={<ServicePlans />} />
                {/* CIR-2026-551: Guided Interface content management (Epic 18 FR-28a, FR-08/FR-09) */}
                <Route path="/staff/guided-content" element={<GuidedContentManagement />} />
                {/* CIR-2026-550: aggregate de-identified guided-journey drop-off report (Epic 14 FR-42) */}
                <Route path="/staff/guided-dropoff-report" element={<GuidedDropoffReport />} />
                {/* CIR-2026-570: live table relationships + derived-data pipelines */}
                <Route path="/staff/schema-map" element={<SchemaMap />} />
              </Route>
            </Route>

            {/* M9: Auditor Portal (token-based auth, no Supabase session).
                CIR-2026-405: the auditor route tree gets its own
                QueryClientProvider (auditorQueryClient) via a pathless layout
                route, so this token-auth portal's React Query cache is fully
                isolated from the staff and client-portal trees. The per-route
                AuditorProvider wrapping is unchanged — the module-scoped token
                (AuditorContext, CIR-2026-447) still persists across in-app
                navigation. */}
            <Route element={<QueryClientProvider client={auditorQueryClient}><Outlet /></QueryClientProvider>}>
              <Route path="/auditor/login" element={<AuditorProvider><AuditorLogin /></AuditorProvider>} />
              <Route path="/auditor/dashboard" element={<AuditorProvider><AuditorDashboard /></AuditorProvider>} />
              <Route path="/auditor/evidence" element={<AuditorProvider><AuditorEvidence /></AuditorProvider>} />
            </Route>

            {/* L4: Client Portal (Supabase Auth, client_users).
                CIR-2026-453: ONE ClientPortalProvider wraps the whole client
                route group via a pathless layout route, so navigating between
                portal pages (and login) keeps the provider mounted instead of
                remounting a fresh session=null provider on every hop. Combined
                with the isLoading guards in each page/layout, this stops the
                authenticated user being ejected to /client-login on hard
                refresh or in-portal navigation.
                CIR-2026-405: this whole client-portal tree also gets its own
                QueryClientProvider (clientPortalQueryClient), isolating its
                React Query cache from the staff and auditor trees. */}
            <Route element={<QueryClientProvider client={clientPortalQueryClient}><ClientPortalProvider><Outlet /></ClientPortalProvider></QueryClientProvider>}>
              <Route path="/client-login" element={<ClientLogin />} />
              <Route path="/client-set-password" element={<ClientSetPassword />} />
              <Route path="/client-reset-password" element={<ClientResetPassword />} />
              {/* CIR-2026-546: Guided Checklist Engine — the login landing page
                  for every client user, either service tier (FR-13-FR-15). */}
              <Route path="/client-portal/guided" element={<GuidedEngine />} />
              {/* CIR-2026-556: certification-record capture surface */}
              <Route path="/client-portal/certifications" element={<GuidedCertifications />} />
              <Route path="/client-portal/dashboard" element={<ClientDashboard />} />
              <Route path="/client-portal/account" element={<ClientAccount />} />
              <Route path="/client-portal/notifications" element={<ClientNotifications />} />
              {/* CIR-2026-528: Self-serve billing management */}
              <Route path="/client-portal/billing" element={<ClientBilling />} />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </ClientProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
